// @ts-nocheck
/* eslint-disable @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import * as d3 from 'd3';
import {
  GREEK_IDX,
  GreeksData,
  OPTION_IDX,
  RawGreeksObject,
  ScannerID,
  ScannerColumnVisibility,
  ScannerExclusiveColumns,
  EquityFieldKey,
  Tick,
  LegacyEquity,
  SynthOIEquity,
} from '../types';
import {
  dayjs,
  formatAsCompactNumber,
  formatAsCurrency,
  formatAsPercentage,
  getDateFormatted,
  predicateSearch,
} from './shared';
import { getIVFromGreeks } from './iVol';
import { defaultColumnScannerVisibility, SCANNERS } from '../config';

export const passesWallMoveFilter = (
  d: LegacyEquity,
  currentWall: keyof LegacyEquity,
  prevWall: keyof LegacyEquity,
  move: 'increase' | 'decrease',
) => {
  const currentValue: number = d[currentWall] as number;
  const prevValue: number = d[prevWall] as number;
  return move === 'increase'
    ? currentValue > prevValue
    : prevValue > currentValue;
};

export const sortByWallMove = (
  a: LegacyEquity,
  b: LegacyEquity,
  currentWall: keyof LegacyEquity,
  prevWall: keyof LegacyEquity,
) => {
  const aCurrentWall = a[currentWall] as number;
  const aPrevWall = a[prevWall] as number;
  const bCurrentWall = b[currentWall] as number;
  const bPrevWall = b[prevWall] as number;

  const aPercentMove = aPrevWall ? (aCurrentWall - aPrevWall) / aPrevWall : 0;
  const bPercentMove = bPrevWall ? (bCurrentWall - bPrevWall) / bPrevWall : 0;

  return bPercentMove - aPercentMove;
};

export const getEquitiesForScanner = (
  scannerID: ScannerID | undefined,
  data: (LegacyEquity | SynthOIEquity)[],
): (LegacyEquity | SynthOIEquity)[] => {
  if (!scannerID) {
    return data;
  }
  const scanner = SCANNERS.get(scannerID);
  if (scanner == null) {
    console.error(`${scannerID} is an invalid scanner`);
    return data.slice();
  }

  let filteredData = data.filter(scanner.passes);
  if (scanner.comparator != null) {
    filteredData.sort(scanner.comparator);
  }
  if (scanner.slice != null) {
    filteredData = filteredData.slice(scanner.slice);
  }
  return filteredData;
};

/** Returns values for callx1, callx2, putx1, putx2 */
export const gNotToPoints = (
  putGNot: number,
  callGNot: number,
  gammas: { call_gnot: number; put_gnot: number; price: number }[],
  ticks: Tick[],
) => {
  // Make max bar 30
  // Convert these to epoch millis
  const timestamps = ticks.map((d) => d.epoch_millis);
  const gnots = gammas
    .map((d) => [d.call_gnot, d.put_gnot, d.call_gnot + d.put_gnot])
    .flat();
  const [minGNot, maxGNot] = [Math.min(...gnots), Math.max(...gnots)];
  const timestampRange = Math.max(...timestamps) - Math.min(...timestamps);
  const [minTimestamp, maxTimestamp] = [
    Math.min(...timestamps),
    Math.min(...timestamps) + 0.25 * timestampRange,
  ];
  const scale = d3
    .scaleLinear()
    .domain([minGNot, maxGNot])
    .range([minTimestamp, maxTimestamp]);
  const baseline = scale(minGNot);

  return [
    baseline,
    Math.round(scale(callGNot)),
    Math.round(scale(callGNot)),
    Math.round(scale(callGNot + putGNot)),
  ];
};

export const priceToPoints = (
  price: number,
  gammas: { call_gnot: number; put_gnot: number; price: number }[],
  ticks: Tick[],
) => {
  const gammaPrices = gammas.map((d) => d.price);
  const tickPrices = ticks.map((d) => d.price);
  const prices = [...gammaPrices, ...tickPrices];

  const [minPrice, maxPrice] = [Math.min(...prices), Math.max(...prices)];
  const padding =
    (maxPrice - minPrice) / gammas.length / (gammas.length > 4 ? 3 : 6);
  return [price - padding, price + padding];
};

export const getIVForTgtDelta = (
  tgtDelta: number,
  dailyGreeks: RawGreeksObject,
) => {
  const allGreekEntries: [number, GreeksData][] = Object.entries(
    dailyGreeks,
  ).map(([strike, greeks]) => [Number(strike), greeks]);
  allGreekEntries.sort((a, b) => a[0] - b[0]);
  const normedDeltas = allGreekEntries.map(
    ([, callPut]: [number, GreeksData]) => {
      const callDelta = callPut[OPTION_IDX.CALL][GREEK_IDX.DELTA];
      const putDelta = callPut[OPTION_IDX.PUT][GREEK_IDX.DELTA];
      return callDelta <= 0.5 ? callDelta : 1 + putDelta;
    },
  );

  const idx = predicateSearch(normedDeltas, (d: number) => d > tgtDelta);
  const lowerIV = getIVFromGreeks(allGreekEntries[idx < 0 ? 0 : idx][1]);
  if (idx < 0 || idx + 1 >= normedDeltas.length) {
    return lowerIV;
  }
  const upperIV = getIVFromGreeks(allGreekEntries[idx + 1][1]);
  const upperDelta = normedDeltas[idx + 1];
  const fraction = (tgtDelta - upperDelta) / (normedDeltas[idx] - upperDelta);
  return d3.interpolateNumber(upperIV, lowerIV)(fraction);
};

export const getColumnsForScanner = (scanner: ScannerID) =>
  Object.keys(defaultColumnScannerVisibility).reduce<ScannerColumnVisibility>(
    (acc, key) => {
      acc[key as EquityFieldKey] =
        ScannerExclusiveColumns[scanner]?.includes(key as EquityFieldKey) ??
        false;
      return acc;
    },
    {} as ScannerColumnVisibility,
  );

export const createDummyEquity = (id: string): LegacyEquity => ({
  [EquityFieldKey.vrp_scanner_high]: 1,
  [EquityFieldKey.squeeze_scanner]: 1,
  [EquityFieldKey.callsum]: Math.random() * 100,
  [EquityFieldKey.putsum]: Math.random() * 100,
  [EquityFieldKey.minfs]: Math.random() * 100,
  [EquityFieldKey.largeCoi]: Math.random() * 100,
  [EquityFieldKey.largePoi]: Math.random() * 100,
  [EquityFieldKey.totaldelta]: Math.random() * 1_000,
  [EquityFieldKey.d95ne]: Math.random() * 100,
  [EquityFieldKey.d25ne]: Math.random() * 100,
  [EquityFieldKey.d95]: Math.random() * 100,
  [EquityFieldKey.d25]: Math.random() * 100,
  [EquityFieldKey.strike_list]: '',
  [EquityFieldKey.cg_list]: '',
  [EquityFieldKey.pg_list]: '',
  [EquityFieldKey.mf_list]: '',
  [EquityFieldKey.smf_list]: '',
  [EquityFieldKey.putctrl]: Math.random() * 100,
  [EquityFieldKey.activity_factor]: Math.random() * 100,
  [EquityFieldKey.position_factor]: Math.random() * 100,
  [EquityFieldKey.call_strikes_list_absg]: '',
  [EquityFieldKey.call_gnot_list_absg]: '',
  [EquityFieldKey.put_strikes_list_absg]: '',
  [EquityFieldKey.put_gnot_list_absg]: '',
  [EquityFieldKey.date]: new Date().toUTCString(),
  [EquityFieldKey.Symbol]: id,
  [EquityFieldKey.sym]: id,
  [EquityFieldKey.quoteDate]: new Date().toUTCString(),
  [EquityFieldKey.prevClose]: Math.random() * 100,
  [EquityFieldKey.wkHigh52]: Math.random() * 100,
  [EquityFieldKey.wkLow52]: Math.random() * 100,
  [EquityFieldKey.putCallRatio]: Math.random() * 100,
  [EquityFieldKey.keyGammaStr]: Math.random() * 100,
  [EquityFieldKey.keyDeltaStr]: Math.random() * 100,
  [EquityFieldKey.hedgeWall]: Math.random() * 100,
  [EquityFieldKey.callWall]: Math.random() * 100,
  [EquityFieldKey.putWall]: Math.random() * 100,
  [EquityFieldKey.prevHedgeWall]: Math.random() * 100,
  [EquityFieldKey.prevCallWall]: Math.random() * 100,
  [EquityFieldKey.prevPutWall]: Math.random() * 100,
  [EquityFieldKey.nextExpGamma]: Math.random() * 100,
  [EquityFieldKey.nextExpDelta]: Math.random() * 100,
  [EquityFieldKey.topGammaExp]: new Date().toUTCString(),
  [EquityFieldKey.topDeltaExp]: new Date().toUTCString(),
  [EquityFieldKey.putCallVolRatio]: Math.random() * 100,
  [EquityFieldKey.callGamma]: Math.random() * 100,
  [EquityFieldKey.putGamma]: Math.random() * 100,
  [EquityFieldKey.gammaRatio]: Math.random(),
  [EquityFieldKey.callDelta]: Math.random() * 100,
  [EquityFieldKey.putDelta]: Math.random() * 100,
  [EquityFieldKey.putVolume]: Math.random() * 100,
  [EquityFieldKey.callVolume]: Math.random() * 100,
  [EquityFieldKey.deltaRatio]: Math.random() * 100,
  [EquityFieldKey.neSkew]: Math.random() * 100,
  [EquityFieldKey.skew]: Math.random() * 100,
  [EquityFieldKey.neCallVolume]: Math.random() * 100,
  [EquityFieldKey.nePutVolume]: Math.random() * 100,
  [EquityFieldKey.optionsImpliedMove]: Math.random() * 100,
  [EquityFieldKey.stock_volume]: Math.random() * 1_000,
  [EquityFieldKey.dpi]: Math.random(),
  [EquityFieldKey.rv30]: Math.random(),
  [EquityFieldKey.iv30]: Math.random() * 100,
  [EquityFieldKey.skewRank]: Math.random(),
  [EquityFieldKey.garchRank]: Math.random(),
  [EquityFieldKey.ivRank]: Math.random(),
  [EquityFieldKey.atm_iv30_pct_chg]: Math.random() * 100,
});

/**
 * Filters equities by active watchlists and scanner, then sorts free-tier
 * rows so that symbols with full data appear first.
 * Shared by CanvasEquitiesScanner, EquityStockScanner, and EquityhubStockScanner.
 */
export const getScannerData = ({
  equities,
  watchlists,
  activeWatchlistIds,
  activeScanner,
  hasAccess,
}: {
  equities: Map<string, LegacyEquity | SynthOIEquity>;
  watchlists: { id: number | null; symbols: string[] }[] | undefined;
  activeWatchlistIds: number[];
  activeScanner: ScannerID | undefined;
  hasAccess: boolean;
}): (LegacyEquity | SynthOIEquity)[] => {
  let resultData = [...equities.values()];

  if (activeWatchlistIds.length > 0) {
    const activeWatchlists =
      watchlists?.filter((w) => activeWatchlistIds.includes(w.id as number)) ??
      [];

    const syms: Set<string> = new Set(resultData.map((w) => w.sym));
    const watchlistSyms = new Set(activeWatchlists.flatMap((w) => w.symbols));

    resultData = [...watchlistSyms]
      .filter((s) => syms.has(s))
      .map((s) => equities.get(s) as LegacyEquity | SynthOIEquity);
  }

  if (activeScanner) {
    resultData = getEquitiesForScanner(activeScanner, resultData);
  }

  if (hasAccess) {
    return resultData;
  }

  return resultData
    .map(
      (e) =>
        [hasKeyEquityFields(e) ? 1 : 0, e] as [
          number,
          LegacyEquity | SynthOIEquity,
        ],
    )
    .sort(([aScore], [bScore]) => bScore - aScore)
    .map(([_score, e]) => e);
};

export const hasKeyEquityFields = (e: LegacyEquity | SynthOIEquity) => {
  const keyFields: EquityFieldKey[] = [
    EquityFieldKey.name,
    EquityFieldKey.sym,
    EquityFieldKey.prevClose,
    EquityFieldKey.stock_volume,
  ];

  return keyFields.every((f) => !!e[f as keyof typeof e]);
};

export const formatEquityFieldValue = (
  field: EquityFieldKey,
  value: any,
): string => {
  if (value == null) {
    return '-'; // no value
  }

  // Earnings date fields
  if (field === EquityFieldKey.earningsDate) {
    return dayjs(value).format('MM-DD h:mm A');
  }

  // Date fields
  if (
    [
      EquityFieldKey.quoteDate,
      EquityFieldKey.topGammaExp,
      EquityFieldKey.topDeltaExp,
      EquityFieldKey.date,
    ].includes(field)
  ) {
    return getDateFormatted(value);
  }

  // Percentage fields
  if (
    [
      EquityFieldKey.dailyChange,
      EquityFieldKey.nextExpGamma,
      EquityFieldKey.nextExpDelta,
      EquityFieldKey.gammaRatio,
      EquityFieldKey.deltaRatio,
      EquityFieldKey.dpi,
      EquityFieldKey.garchRank,
      EquityFieldKey.skewRank,
      EquityFieldKey.ivRank,
      EquityFieldKey.rv30,
      EquityFieldKey.iv30,
      EquityFieldKey.pskew_pct,
      EquityFieldKey.cskew_pct,
      EquityFieldKey.atm_iv30_pct_chg,
    ].includes(field)
  ) {
    return formatAsPercentage(value);
  }

  // Currency fields
  if (
    [
      EquityFieldKey.price,
      EquityFieldKey.prevClose,
      EquityFieldKey.wkHigh52,
      EquityFieldKey.wkLow52,
      EquityFieldKey.keyGammaStr,
      EquityFieldKey.keyDeltaStr,
      EquityFieldKey.hedgeWall,
      EquityFieldKey.callWall,
      EquityFieldKey.putWall,
      EquityFieldKey.optionsImpliedMove,
      EquityFieldKey.high_vol_point,
      EquityFieldKey.low_vol_point,
    ].includes(field as EquityFieldKey)
  ) {
    return formatAsCurrency(value);
  }

  // Default number formatting
  if (typeof value === 'number') {
    return formatAsCompactNumber(value, {
      maximumFractionDigits: 2,
    });
  }

  // Default string return
  return String(value);
};

export const SORT_FOR_SCANNER_MAP: Map<
  ScannerID,
  {
    field: EquityFieldKey;
    sort: 'desc' | 'asc';
  }
> = new Map([
  [ScannerID.VOL_RISK_PREMIUM, { field: EquityFieldKey.ivRank, sort: 'desc' }],
  [
    ScannerID.HIGHEST_OPTIONS_IMPACT,
    { field: EquityFieldKey.optionsImpact, sort: 'desc' },
  ],
  [ScannerID.EARNINGS_IV_CRUSH, { field: EquityFieldKey.ivRank, sort: 'desc' }],
  [
    ScannerID.REVERSE_VOL_RISK_PREMIUM,
    { field: EquityFieldKey.ivRank, sort: 'asc' },
  ],
]);
